import { Box } from '@mui/material';
import { colors, mergeChrome } from 'src/theme';

interface SwatchLegendProps {
  /**
   * Accessible name. The legend is rendered once per section, so the two need
   * distinguishable names - two lists both called "Colour legend" are
   * indistinguishable to anyone navigating by landmark.
   */
  ariaLabel?: string;
  /** Swatch class names to leave out (for a section that cannot show them). */
  omit?: string[];
  /** Replacement labels by swatch class name. */
  relabel?: Record<string, string>;
  /** Replacement grid positions by swatch class name. */
  place?: Record<string, { column: number; row: number }>;
}

// Where each swatch sits: a column (left to right) and a row within it. Columns
// are renumbered over the swatches actually shown, so an omitted column leaves
// no gap.
const LAYOUT: Record<string, { column: number; row: number }> = {
  'swatch-red': { column: 1, row: 1 },
  'swatch-grey': { column: 1, row: 2 },
  'swatch-blue': { column: 2, row: 1 },
  'swatch-brown': { column: 2, row: 2 },
  'swatch-resolved': { column: 3, row: 1 },
};

const SwatchLegend = ({
  ariaLabel = 'Colour legend',
  omit = [],
  relabel = {},
  place = {},
}: SwatchLegendProps) => {
  const positionOf = (className: string) =>
    place[className] ?? LAYOUT[className];
  // Every swatch carries a border, not just the white one: the mockup's tones
  // are pale tints, so an unbordered block would read as empty space.
  const swatches = [
    {
      tone: colors.conflict.grey,
      label: 'No Conflict',
      className: 'swatch-grey',
    },
    {
      tone: colors.conflict.blue,
      label: 'Your Value Changed',
      className: 'swatch-blue',
    },
    {
      tone: colors.conflict.brown,
      label: 'Their Value Changed',
      className: 'swatch-brown',
    },
    {
      tone: colors.conflict.red,
      label: 'Conflict',
      className: 'swatch-red',
    },
    {
      tone: colors.conflict.resolved,
      label: 'Decided',
      className: 'swatch-resolved',
      bold: true,
    },
  ];

  const visible = swatches.filter((swatch) => !omit.includes(swatch.className));
  const columns = Array.from(
    new Set(visible.map((swatch) => positionOf(swatch.className).column))
  ).sort((a, b) => a - b);

  return (
    <Box
      component="ul"
      role="list"
      aria-label={ariaLabel}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns.length}, auto)`,
        justifyContent: 'end',
        columnGap: 3,
        rowGap: 0.5,
        listStyle: 'none',
        m: 0,
        p: 0,
        alignItems: 'center',
      }}
    >
      {visible.map((swatch) => (
        <Box
          component="li"
          key={swatch.className}
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            gridColumn:
              columns.indexOf(positionOf(swatch.className).column) + 1,
            gridRow: positionOf(swatch.className).row,
          }}
        >
          <Box
            className={swatch.className}
            sx={{
              width: 24,
              height: 24,
              backgroundColor: swatch.tone.bg,
              color: swatch.tone.fg,
              border: `1px solid ${mergeChrome.dividerStrong}`,
              fontWeight: swatch.bold ? 700 : 'inherit',
              flexShrink: 0,
            }}
          />
          <Box sx={{ fontSize: '0.875rem' }}>
            {relabel[swatch.className] ?? swatch.label}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SwatchLegend;
